<template>
  <nav class="portal-navbar">
    <div class="navbar-inner">
      <!-- Logo 区域 -->
      <div class="navbar-brand" @click="navigateTo('home')">
        <img class="brand-logo" :src="brandLogo" alt="紫金山 AI" />
      </div>

      <!-- 导航菜单 -->
      <div class="navbar-menu">
        <a
          v-for="item in navItems"
          :key="item.key"
          class="nav-link"
          :class="{ active: activeNav === item.key, 'has-dropdown': item.hasDropdown }"
          @click="navigateTo(item.key)"
          @mouseenter="item.hasDropdown ? showMegaMenu(item.key) : undefined"
          @mouseleave="item.hasDropdown ? startHideTimer() : undefined"
        >
          {{ item.label }}
          <Icon v-if="item.hasDropdown" icon="lucide:chevron-down" :size="14" class="nav-chevron" />
        </a>
      </div>

      <!-- 右侧操作区 -->
      <div class="navbar-actions">
        <button class="icon-btn" title="搜索" @click="handleSearch">
          <Icon icon="lucide:search" :size="18" />
        </button>
        <a class="nav-service-phone" href="tel:4006465288" aria-label="拨打客服热线：400-646-5288">
          <Icon icon="lucide:phone" :size="15" />
          <span>400-646-5288</span>
        </a>
        <button class="btn-outline" @click="openCustomerService">免费预约</button>
        <button class="btn-primary" @click="handleLogin">登录</button>
        <button class="btn-primary" @click="handleRegister">注册</button>
      </div>

      <button
        class="mobile-menu-button"
        :aria-expanded="mobileMenuVisible"
        aria-controls="portal-mobile-menu"
        :aria-label="mobileMenuVisible ? '关闭导航菜单' : '打开导航菜单'"
        @click="toggleMobileMenu"
      >
        <Icon :icon="mobileMenuVisible ? 'lucide:x' : 'lucide:menu'" :size="22" />
      </button>
    </div>

    <div v-if="mobileMenuVisible" class="mobile-menu-backdrop" @click="closeMobileMenu"></div>
    <div id="portal-mobile-menu" class="mobile-menu-panel" :class="{ visible: mobileMenuVisible }">
      <div class="mobile-menu-links">
        <button
          v-for="item in navItems"
          :key="item.key"
          :class="{ active: activeNav === item.key }"
          @click="navigateTo(item.key)"
        >
          <span>{{ item.label }}</span>
          <Icon icon="lucide:chevron-right" :size="17" />
        </button>
      </div>
      <div class="mobile-menu-actions">
        <a href="tel:4006465288"><Icon icon="lucide:phone" :size="17" />400-646-5288</a>
        <button class="mobile-trial-button" @click="openCustomerService">免费预约</button>
        <button class="mobile-login-button" @click="handleLogin">登录</button>
        <button class="mobile-register-button" @click="handleRegister">注册</button>
      </div>
    </div>
  </nav>

  <!-- Mega Menu 下拉面板 -->
  <Teleport to="body">
    <transition name="mega-menu-fade">
      <div
        v-show="megaMenuVisible"
        class="mega-menu-overlay"
        :class="{ 'is-mega-wide': isMegaWide }"
        @mouseenter="clearHideTimer()"
        @mouseleave="startHideTimer()"
      >
        <div class="mega-menu-panel" :class="{ 'is-mega-wide': isMegaWide }">
          <!-- AI产品：左分类 + 中入口网格（无右侧精选） -->
          <template v-if="activeMegaMenu === 'products'">
            <div class="mega-solutions-layout">
              <nav class="mega-solutions-sidebar" aria-label="产品分类">
                <button
                  v-for="tab in productTabs"
                  :key="tab"
                  type="button"
                  class="mega-solutions-tab"
                  :class="{ active: activeProductTab === tab }"
                  @mouseenter="activeProductTab = tab"
                  @click="activeProductTab = tab"
                >
                  {{ tab }}
                </button>
              </nav>

              <div class="mega-solutions-content mega-solutions-content--no-feature">
                <div class="mega-solutions-main">
                  <div class="mega-solutions-grid">
                    <a
                      v-for="p in activeProductItems"
                      :key="p.id"
                      class="mega-solution-item"
                      @click="navigateToProduct(p.path)"
                    >
                      <div
                        class="mega-solution-icon"
                        :style="`background:${p.bg || p.color + '18'};color:${p.color}`"
                      >
                        <img
                          v-if="isPortalIconImage(p.icon)"
                          class="mega-solution-icon-img"
                          :src="p.icon"
                          :alt="p.name"
                        />
                        <Icon v-else :icon="p.icon || 'lucide:box'" :size="22" />
                      </div>
                      <div class="mega-solution-text">
                        <p class="mega-solution-name">{{ p.name }}</p>
                        <p class="mega-solution-desc">{{ p.descShort }}</p>
                      </div>
                    </a>
                  </div>
                  <a class="mega-footer-link mega-footer-brand" @click="navigateTo('products')">
                    查看全部产品 →
                  </a>
                </div>
              </div>
            </div>
          </template>

          <!-- 解决方案：左分类通栏底色 + 顶对齐入口网格 + 精选案例 -->
          <template v-if="activeMegaMenu === 'solutions'">
            <div class="mega-solutions-layout">
              <nav class="mega-solutions-sidebar" aria-label="解决方案分类">
                <button
                  v-for="tab in solutionTabs"
                  :key="tab.key"
                  type="button"
                  class="mega-solutions-tab"
                  :class="{ active: activeSolutionTab === tab.key }"
                  @mouseenter="activeSolutionTab = tab.key"
                  @click="activeSolutionTab = tab.key"
                >
                  {{ tab.label }}
                </button>
              </nav>

              <div class="mega-solutions-content">
                <div class="mega-solutions-main">
                  <div class="mega-solutions-grid">
                    <a
                      v-for="item in activePanelItems"
                      :key="item.id"
                      class="mega-solution-item"
                      @click="navigateMegaItem(item)"
                    >
                      <div
                        class="mega-solution-icon"
                        :style="`background:${item.color}18;color:${item.color}`"
                      >
                        <img
                          v-if="isPortalIconImage(item.icon)"
                          class="mega-solution-icon-img"
                          :src="item.icon"
                          :alt="item.title"
                        />
                        <Icon v-else :icon="item.icon || 'lucide:layers'" :size="22" />
                      </div>
                      <div class="mega-solution-text">
                        <p class="mega-solution-name">{{ item.title }}</p>
                        <p class="mega-solution-desc">{{ item.tagline }}</p>
                      </div>
                    </a>
                  </div>
                </div>

                <aside
                  v-if="featuredCase"
                  class="mega-featured-case"
                  @click="navigateMegaItem(featuredCase)"
                >
                  <div
                    class="mega-featured-cover"
                    :class="{ 'is-icon': !featuredCase.image }"
                    :style="
                      !featuredCase.image
                        ? `background:${featuredCase.color || '#0066ff'}14;color:${featuredCase.color || '#0066ff'}`
                        : undefined
                    "
                  >
                    <img
                      v-if="featuredCase.image"
                      :src="featuredCase.image"
                      :alt="featuredCase.title"
                    />
                    <Icon v-else icon="lucide:layers" :size="40" />
                  </div>
                  <div class="mega-featured-body">
                    <h3 class="mega-featured-title">{{ featuredCase.title }}</h3>
                    <p class="mega-featured-desc">{{ featuredCase.desc }}</p>
                    <span class="mega-featured-link">
                      立即查看
                      <Icon icon="lucide:chevron-right" :size="14" />
                    </span>
                  </div>
                </aside>
              </div>
            </div>
          </template>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script lang="ts" setup>
import { openCustomerService as openCustomerServicePopup } from './openCustomerService'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Icon } from '@/components/Icon'
import { getAdminAccessToken } from '@/utils/auth'
import { usePortalState } from './usePortalState'
import {
  solutions as staticSolutions,
  solutionDetails,
  products as staticProducts
} from './data/portalData'
import {
  DEFAULT_SOLUTION_TABS,
  getPortalProductNavigation,
  getPortalSolutionCatalog,
  getPortalSolutionTabs,
  isPortalIconImage,
  type PortalSolutionCatalogItem,
  type PortalSolutionCategoryKey,
  type PortalSolutionTabItem
} from './data/portalCatalog'
import brandLogo from '@/assets/portal/home/brand-logo-blue.png'
import solutionRetail from '@/assets/portal/home/solution-retail.png'
import solutionManufacturing from '@/assets/portal/home/solution-manufacturing.png'
import solutionEducation from '@/assets/portal/home/solution-education.png'
import solutionHealthcare from '@/assets/portal/home/solution-healthcare.png'
import solutionFinance from '@/assets/portal/home/solution-finance.png'
import solutionHospitality from '@/assets/portal/home/solution-hospitality.png'

defineOptions({ name: 'PortalNavbar' })

const router = useRouter()
const route = useRoute()
const { openSearchModal, setActiveNav, getActiveNav } = usePortalState()

const activeNav = computed(() => getActiveNav.value)
const navItems: Array<{ key: string; label: string; hasDropdown?: boolean }> = [
  { key: 'home', label: '首页' },
  { key: 'claw', label: 'ZJSClaw' },
  { key: 'products', label: 'AI产品', hasDropdown: true },
  { key: 'solutions', label: '解决方案', hasDropdown: true },
  { key: 'partners', label: '合作伙伴' },
  { key: 'help-center', label: '帮助中心' },
  { key: 'about', label: '关于我们' },
  { key: 'contact', label: '联系我们' }
]

// 导航路径映射
const navPathMap: Record<string, string> = {
  home: '/portal',
  claw: '/portal/zjsclaw',
  products: '/portal/products',
  solutions: '/portal/solutions',
  partners: '/portal/partners',
  'help-center': '/portal/help-center',
  about: '/portal/about',
  contact: '/portal/contact'
}

// ===== Mega Menu 状态 =====
const megaMenuVisible = ref(false)
// 控制窄屏设备的侧滑导航显隐。
const mobileMenuVisible = ref(false)
const activeMegaMenu = ref<'products' | 'solutions'>('products')
const activeSolutionTab = ref<PortalSolutionCategoryKey>('ecommerce')
const activeProductTab = ref('')
let hideTimer: ReturnType<typeof setTimeout> | null = null

const isMegaWide = computed(
  () => activeMegaMenu.value === 'solutions' || activeMegaMenu.value === 'products'
)

type DropdownProduct = {
  id: string
  name: string
  icon: string
  color: string
  bg: string
  descShort: string
  path: string
  category: string
}

const PRODUCT_TAB_ORDER = ['建站营销', '电商交易', '用户运营', 'AI智能', '内容社区']
const PRODUCT_PER_TAB = 12

function buildStaticDropdownProducts(): DropdownProduct[] {
  return staticProducts.map((p) => ({
    id: p.id,
    name: p.name,
    icon: p.icon,
    color: p.color,
    bg: p.bgColor,
    descShort: p.descShort || p.desc,
    path: `/portal/products/${p.id}`,
    category: p.category || '其他'
  }))
}

function productTabOrderIndex(name: string) {
  const i = PRODUCT_TAB_ORDER.indexOf(name)
  return i >= 0 ? i : PRODUCT_TAB_ORDER.length + 1
}

function preferProductTab(tabs: string[]) {
  return tabs.find((t) => t === 'AI智能') || tabs[0] || ''
}

function syncProductTab(tabs: string[]) {
  if (!tabs.length) {
    activeProductTab.value = ''
    return
  }
  if (!tabs.includes(activeProductTab.value)) {
    activeProductTab.value = preferProductTab(tabs)
  }
}

function handleSearch() {
  openSearchModal()
}

function preferSolutionTabKey() {
  return (
    solutionTabs.value.find((t) => t.key === 'ecommerce')?.key ||
    solutionTabs.value[0]?.key ||
    'ecommerce'
  )
}

function showMegaMenu(key: string) {
  clearHideTimer()
  activeMegaMenu.value = key as 'products' | 'solutions'
  if (key === 'solutions') activeSolutionTab.value = preferSolutionTabKey()
  if (key === 'products') syncProductTab(productTabs.value)
  megaMenuVisible.value = true
}

function startHideTimer() {
  hideTimer = setTimeout(() => {
    megaMenuVisible.value = false
  }, 200)
}

function clearHideTimer() {
  if (hideTimer) {
    clearTimeout(hideTimer)
    hideTimer = null
  }
}

function closeMegaMenu() {
  megaMenuVisible.value = false
}

function toggleMobileMenu() {
  mobileMenuVisible.value = !mobileMenuVisible.value
}

function closeMobileMenu() {
  mobileMenuVisible.value = false
}

function resetPortalScroll() {
  const el = document.querySelector('.portal-page') as HTMLElement | null
  if (el) el.scrollTop = 0
  window.scrollTo(0, 0)
}

// ===== 导航方法 =====
function navigateTo(key: string) {
  closeMegaMenu()
  closeMobileMenu()
  const path = navPathMap[key] || '/portal'
  // ZJSClaw 落地页新窗口打开，当前门户页保持不动
  if (key === 'claw') {
    window.open(path, '_blank', 'noopener,noreferrer')
    return
  }
  setActiveNav(key as any)
  router.push(path).finally(() => {
    resetPortalScroll()
    nextTick(() => resetPortalScroll())
  })
}

function navigateToProduct(path: string) {
  closeMegaMenu()
  router.push(path).finally(() => {
    resetPortalScroll()
    nextTick(() => resetPortalScroll())
  })
}

// ===== 下拉产品数据（按 category 分组；接口失败用静态兜底） =====
const dropdownProducts = ref<DropdownProduct[]>(buildStaticDropdownProducts())

const productTabs = computed(() => {
  const seen = new Set<string>()
  const tabs: string[] = []
  for (const p of dropdownProducts.value) {
    const cat = (p.category || '其他').trim() || '其他'
    if (seen.has(cat)) continue
    seen.add(cat)
    tabs.push(cat)
  }
  return tabs.sort((a, b) => productTabOrderIndex(a) - productTabOrderIndex(b) || a.localeCompare(b, 'zh'))
})

const activeProductItems = computed(() => {
  const tab = activeProductTab.value
  if (!tab) return []
  return dropdownProducts.value.filter((p) => (p.category || '其他') === tab).slice(0, PRODUCT_PER_TAB)
})

syncProductTab(productTabs.value)

type MegaPanelItem = {
  id: string
  title: string
  tagline: string
  icon: string
  color: string
  path: string
  image?: string
  featuredDesc?: string
  featured?: boolean
}

const solutionTabs = ref<PortalSolutionTabItem[]>([...DEFAULT_SOLUTION_TABS])

const solutionCoverMap: Record<string, string> = {
  retail: solutionRetail,
  manufacturing: solutionManufacturing,
  education: solutionEducation,
  healthcare: solutionHealthcare,
  finance: solutionFinance,
  catering: solutionHospitality,
  hospitality: solutionHospitality
}

function shortSolutionName(name: string) {
  return name.replace(/解决方案$/, '').trim() || name
}

function solutionTagline(desc: string, effect?: string) {
  if (effect) return effect
  const compact = desc.replace(/[，,。；;]/g, '，').split('，')[0] || desc
  return compact.length > 16 ? `${compact.slice(0, 16)}…` : compact
}

function mapCatalogToMega(item: PortalSolutionCatalogItem): MegaPanelItem {
  return {
    id: item.id,
    title: item.shortName || shortSolutionName(item.name),
    tagline: solutionTagline(item.desc, item.effect),
    icon: item.icon || 'lucide:layers',
    color: item.color || '#0066FF',
    path: item.path,
    image: item.coverImage || solutionCoverMap[item.id],
    featuredDesc: item.descLong || item.desc || item.effect,
    featured: item.featured
  }
}

function mapIndustryItem(item: {
  id: string
  name: string
  icon: string
  color: string
  desc: string
  effect?: string
  descLong?: string
  path?: string
}): MegaPanelItem {
  return {
    id: item.id,
    title: shortSolutionName(item.name),
    tagline: solutionTagline(item.desc, item.effect),
    icon: item.icon,
    color: item.color,
    path: item.path || `/portal/solutions/${item.id}`,
    image: solutionCoverMap[item.id],
    featuredDesc: item.descLong || item.desc
  }
}

/** 按分类缓存入口；接口有数据则覆盖/新增对应 key（支持运营自增 Tab） */
const panelItemsByTab = ref<Record<string, MegaPanelItem[]>>({
  industry: staticSolutions.slice(0, 8).map((item) => mapIndustryItem(item)),
  cases: [],
  ai: [],
  biz: []
})

function buildStaticCaseItems(): MegaPanelItem[] {
  const items: MegaPanelItem[] = []
  for (const sol of staticSolutions) {
    const detail = solutionDetails[sol.id]
    const cover = solutionCoverMap[sol.id]
    for (const [idx, c] of (detail?.customerCases || []).entries()) {
      items.push({
        id: `${sol.id}-case-${idx}`,
        title: c.name,
        tagline: c.result,
        icon: sol.icon,
        color: sol.color,
        path: `/portal/solutions/${sol.id}`,
        image: cover,
        featuredDesc: c.result
      })
    }
  }
  return items.slice(0, 8)
}

function buildStaticAiItems(): MegaPanelItem[] {
  const core: MegaPanelItem[] = [
    {
      id: 'ai-tools',
      title: 'AI工具',
      tagline: '智简高效，全能陪伴',
      icon: 'lucide:wrench',
      color: '#0667f9',
      path: '/portal/products',
      image: solutionRetail,
      featuredDesc: '智简未来，效能倍增，你的全天候副手'
    },
    {
      id: 'ai-enterprise',
      title: 'AI企业应用',
      tagline: '行业场景，专属智能',
      icon: 'lucide:building-2',
      color: '#2f7cf6',
      path: '/portal/products',
      image: solutionHealthcare,
      featuredDesc: '行业场景深度适配，打造专属智能应用'
    },
    {
      id: 'ai-app',
      title: 'AI应用中台',
      tagline: '敏捷连接，业务创新',
      icon: 'lucide:layers',
      color: '#08bd72',
      path: '/portal/products',
      image: solutionEducation,
      featuredDesc: '敏捷连接智能，业务创新随心所欲'
    },
    {
      id: 'ai-biz',
      title: 'AI业务中台',
      tagline: '数智驱动，全局协同',
      icon: 'lucide:briefcase',
      color: '#8154ef',
      path: '/portal/biz',
      image: solutionManufacturing,
      featuredDesc: '数智驱动决策，全局协同，赢在当下'
    },
    {
      id: 'ai-data',
      title: 'AI数据中台',
      tagline: '融通数据，炼就洞察',
      icon: 'lucide:database',
      color: '#ff9100',
      path: '/portal/data',
      image: solutionFinance,
      featuredDesc: '融通数据孤岛，炼就洞察，赋能万变'
    }
  ]
  const more = staticProducts
    .filter((p) => p.category === 'AI智能')
    .slice(0, 4)
    .map((p) => ({
      id: p.id,
      title: p.name,
      tagline: p.descShort || p.desc,
      icon: p.icon,
      color: p.color,
      path: `/portal/products/${p.id}`,
      featuredDesc: p.desc
    }))
  return [...core, ...more].slice(0, 8)
}

const staticBizItems: MegaPanelItem[] = [
  {
    id: 'oa',
    title: 'OA办公',
    tagline: '流程审批，协同办公',
    icon: 'lucide:briefcase',
    color: '#0066FF',
    path: '/portal/biz',
    image: solutionEducation,
    featuredDesc: '覆盖企业日常办公全流程，流程审批、公文与会议管理一体化'
  },
  {
    id: 'crm',
    title: 'CRM客户',
    tagline: '线索商机，客户经营',
    icon: 'lucide:users',
    color: '#00B96B',
    path: '/portal/biz',
    image: solutionRetail,
    featuredDesc: '全链路客户关系管理，线索、商机与客户经营一体化'
  },
  {
    id: 'erp',
    title: 'ERP资源',
    tagline: '采购库存，财务一体',
    icon: 'lucide:box',
    color: '#FF9500',
    path: '/portal/biz',
    image: solutionFinance,
    featuredDesc: '采购、库存、销售、财务一体化，企业资源计划数字化'
  },
  {
    id: 'mes',
    title: 'MES制造',
    tagline: '车间执行，质量追溯',
    icon: 'lucide:cpu',
    color: '#7C3AED',
    path: '/portal/biz',
    image: solutionManufacturing,
    featuredDesc: '车间级制造执行，工单、质检与实时看板一站打通'
  },
  {
    id: 'srm',
    title: 'SRM供应',
    tagline: '供应商协同，采购寻源',
    icon: 'lucide:truck',
    color: '#0891B2',
    path: '/portal/biz',
    image: solutionHospitality,
    featuredDesc: '供应商全生命周期管理，准入、寻源与合同协同'
  }
]

panelItemsByTab.value.cases = buildStaticCaseItems()
panelItemsByTab.value.ai = buildStaticAiItems()
panelItemsByTab.value.biz = [...staticBizItems]

const activePanelItems = computed(() =>
  (panelItemsByTab.value[activeSolutionTab.value] || []).slice(0, 16)
)

const featuredCase = computed(() => {
  const list = activePanelItems.value
  if (!list.length) return null
  const preferred = list.find((s) => s.featured) || list[0]
  return {
    id: preferred.id,
    title: preferred.title,
    tagline: preferred.tagline,
    desc: preferred.featuredDesc || preferred.tagline,
    icon: preferred.icon,
    color: preferred.color,
    path: preferred.path,
    image: preferred.image || ''
  }
})

function navigateMegaItem(item: { path: string }) {
  closeMegaMenu()
  const path = (item.path || '').trim()
  if (!path) return
  // 方案入口一律新窗口打开（内链 / 外链）
  window.open(path, '_blank', 'noopener,noreferrer')
}

function applySolutionCatalog(catalog: PortalSolutionCatalogItem[]) {
  const grouped = new Map<string, PortalSolutionCatalogItem[]>()
  for (const item of catalog) {
    const key = item.categoryKey || 'industry'
    if (!grouped.has(key)) grouped.set(key, [])
    grouped.get(key)!.push(item)
  }
  for (const [key, items] of grouped) {
    if (key === 'industry') {
      const featured = items.filter((item) => item.featured)
      const pick = (featured.length ? featured : items).slice(0, 16)
      panelItemsByTab.value[key] = pick.map(mapCatalogToMega)
    } else {
      panelItemsByTab.value[key] = items.slice(0, 16).map(mapCatalogToMega)
    }
  }
}

onMounted(async () => {
  const [productResult, solutionResult, tabResult] = await Promise.allSettled([
    getPortalProductNavigation(),
    getPortalSolutionCatalog(),
    getPortalSolutionTabs()
  ])

  if (productResult.status === 'fulfilled' && productResult.value.length) {
    dropdownProducts.value = productResult.value.map((item) => ({
      id: item.id,
      name: item.name,
      icon: item.icon,
      color: item.color,
      bg: item.bgColor,
      descShort: item.descShort,
      path: item.path,
      category: item.category || '其他'
    }))
    syncProductTab(productTabs.value)
  } else {
    syncProductTab(productTabs.value)
  }
  if (tabResult.status === 'fulfilled' && tabResult.value.length) {
    // 左栏分类 = 后台已发布 tab/list
    solutionTabs.value = tabResult.value.filter((t) => t.enabled)
    if (!solutionTabs.value.some((t) => t.key === activeSolutionTab.value)) {
      activeSolutionTab.value = preferSolutionTabKey()
    }
  }
  if (solutionResult.status === 'fulfilled' && solutionResult.value.length) {
    applySolutionCatalog(solutionResult.value)
  }
})

function handleLogin() {
  // 已登录 → 带 from=portal 让 LoginPage 显示"进入管理平台"面板
  const extra: Record<string, string> = getAdminAccessToken() ? { from: 'portal' } : {}
  openAuthPage('login', extra)
  closeMobileMenu()
}

function handleRegister() {
  openAuthPage('register')
  closeMobileMenu()
}

function openAuthPage(mode: 'login' | 'register', extraQuery?: Record<string, string>) {
  const redirect = typeof route.query.redirect === 'string' ? route.query.redirect : ''
  const query: Record<string, string> = { mode, account: 'member', ...extraQuery }
  if (redirect) {
    query.redirect = redirect
  }
  const href = router.resolve({ path: '/login', query }).href
  window.open(href, '_blank', 'noopener,noreferrer')
}

function openCustomerService() {
  openCustomerServicePopup()
  closeMobileMenu()
}
</script>

<style lang="scss" scoped>
.portal-navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: #fff;
  z-index: 1000;
  display: flex;
  align-items: center;
}

.navbar-inner {
  max-width: 1500px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px;
  box-sizing: border-box;
}

.navbar-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  flex-shrink: 0;
}

.brand-logo {
  width: 154px;
  height: 44px;
  object-fit: contain;
}

.brand-name {
  font-family: 'Noto Sans SC', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  letter-spacing: 0;
}

.navbar-menu {
  display: flex;
  align-items: center;
  gap: 22px;
}

.nav-link {
  font-size: 13px;
  font-weight: 500;
  color: #4b5563;
  cursor: pointer;
  text-decoration: none;
  transition: color 0.2s;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover,
  &.active {
    color: #0066ff;
  }
}

.nav-chevron {
  flex-shrink: 0;
}

.navbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.nav-service-phone {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 16px;
  background: #0066ff;
  box-shadow: 0 3px 10px rgb(0 102 255 / 20%);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  transition:
    background-color 0.2s,
    box-shadow 0.2s,
    transform 0.2s;

  &:hover {
    background: #0056d9;
    box-shadow: 0 5px 14px rgb(0 102 255 / 28%);
    color: #fff;
    transform: translateY(-1px);
  }
}

.language-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4b5563;
  transition: color 0.2s;

  &:hover {
    color: #087af4;
  }
}

.btn-text {
  font-size: 12px;
  font-weight: 500;
  color: #2f3745;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 7px 6px;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    color: #0066ff;
  }
}

.download-btn {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.btn-outline {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  background: #fff;
  border: 1px solid #d1d5db;
  cursor: pointer;
  padding: 6px 13px;
  border-radius: 8px;
  transition: all 0.3s;
  white-space: nowrap;

  &:hover {
    border-color: #0066ff;
    color: #0066ff;
    transform: translateY(-1px);
  }
}

.btn-primary {
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  background: #0066ff;
  border: none;
  cursor: pointer;
  padding: 6px 13px;
  border-radius: 8px;
  transition: all 0.3s;
  white-space: nowrap;

  &:hover {
    background: #0044cc;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 102, 255, 0.35);
  }
}

// ===== Mega Menu 下拉面板 =====
.mega-menu-overlay {
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
  background: #fff;
  box-shadow:
    0 12px 40px rgba(0, 0, 0, 0.1),
    0 2px 8px rgba(0, 0, 0, 0.05);
  border-top: 1px solid #f3f4f6;
  z-index: 999;

  // 宽面板（解决方案 / AI产品）：左侧从视口边缘到分类栏，整段铺 #f8f9fd
  &.is-mega-wide {
    background: linear-gradient(
      to right,
      #f8f9fd 0,
      #f8f9fd calc((100% - min(100%, 1500px)) / 2 + 200px),
      #fff calc((100% - min(100%, 1500px)) / 2 + 200px)
    );
  }
}

.mega-menu-panel {
  max-width: 1500px;
  margin: 0 auto;
  padding: 32px 24px;

  &.is-mega-wide {
    padding: 0;
  }
}

// 产品网格（旧平铺，保留类名以免其它引用；Mega 已改用 solutions 布局）
.mega-products-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 12px;
}

.mega-product-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.2s;
  min-width: 0;

  &:hover {
    background: #f9fafb;
  }
}

.mega-product-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.mega-product-text {
  min-width: 0;
}

.mega-product-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 2px;
}

.mega-product-desc {
  font-size: 12px;
  color: #9ca3af;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

// 解决方案 / AI产品：左分类通栏 #f8f9fd + 中栏顶对齐
.mega-solutions-layout {
  display: grid;
  grid-template-columns: 200px minmax(0, 1fr);
  align-items: stretch;
  min-height: 320px;
}

.mega-solutions-sidebar {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 28px 16px;
  background: #f8f9fd;
  border-right: 1px solid #eef0f5;
  position: relative;
  z-index: 1;
}

.mega-solutions-tab {
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #4b5563;
  cursor: pointer;
  transition:
    background 0.2s,
    color 0.2s;

  &:hover {
    color: #0066ff;
    background: #eef4ff;
  }

  &.active {
    color: #0066ff;
    background: #e8f1ff;
    font-weight: 600;
  }
}

.mega-solutions-content {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 280px;
  gap: 24px;
  align-items: start;
  padding: 28px 28px 28px 24px;
  min-width: 0;
  background: #fff;

  &--no-feature {
    grid-template-columns: minmax(0, 1fr);
  }
}

.mega-solutions-main {
  min-width: 0;
}

.mega-solutions-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 4px 8px;
  width: 100%;
  align-content: start;
}

.mega-solution-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 12px;
  border-radius: 10px;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.2s;

  &:hover {
    background: #f5f8ff;

    .mega-solution-name {
      color: #0066ff;
    }
  }
}

.mega-solution-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}

.mega-solution-icon-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.mega-solution-text {
  min-width: 0;
  padding-top: 1px;
}

.mega-solution-name {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 4px;
  line-height: 1.3;
  transition: color 0.2s;
}

.mega-solution-desc {
  font-size: 12px;
  color: #9ca3af;
  margin: 0;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.mega-featured-case {
  border: 1px solid #e8ecf1;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  min-height: 260px;
  transition:
    box-shadow 0.2s,
    border-color 0.2s;

  &:hover {
    border-color: #c7d7fe;
    box-shadow: 0 8px 24px rgba(0, 102, 255, 0.1);

    .mega-featured-link {
      gap: 6px;
    }
  }
}

.mega-featured-cover {
  height: 132px;
  overflow: hidden;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;

  &.is-icon {
    height: 120px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.mega-featured-body {
  padding: 16px 16px 18px;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.mega-featured-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 8px;
  line-height: 1.35;
}

.mega-featured-desc {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.6;
  margin: 0 0 14px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
}

.mega-featured-link {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 13px;
  font-weight: 600;
  color: #0066ff;
  transition: gap 0.2s;
}

// 底部链接
.mega-footer-link {
  display: block;
  text-align: center;
  margin-top: 16px;
  padding: 12px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  text-decoration: none;

  &.mega-footer-brand {
    background: #ebf5ff;
    color: #0066ff;
    margin-bottom: 8px;
    transition: background 0.2s;

    &:hover {
      background: #dbeafe;
    }
  }

  &.mega-footer-gray {
    background: #f9fafb;
    color: #4b5563;
    transition: background 0.2s;

    &:hover {
      background: #f3f4f6;
    }
  }
}

// 过渡动画
.mega-menu-fade-enter-active {
  transition: all 0.3s ease;
}

.mega-menu-fade-leave-active {
  transition: all 0.25s ease;
}

.mega-menu-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.mega-menu-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
.icon-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #f3f4f6;
  transition: background 0.2s;
  border: none;
  cursor: pointer;
  &:hover {
    color: #087af4;
  }
}

.mobile-menu-button,
.mobile-menu-panel,
.mobile-menu-backdrop {
  display: none;
}

// 响应式：≤1024 隐藏桌面菜单并显示汉堡，避免 700–1024 导航断档
@media (max-width: 1024px) {
  .portal-navbar {
    height: 56px;
  }

  .navbar-inner {
    box-sizing: border-box;
    padding: 0 16px;
    gap: 10px;
  }

  .navbar-menu {
    display: none;
  }

  .mobile-menu-button {
    display: inline-flex;
    width: 40px;
    height: 40px;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: #f3f6fa;
    color: #182338;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: relative;
    z-index: 1002;
    flex-shrink: 0;

    span {
      font-size: 24px;
      line-height: 1;
    }

    :deep(svg) + span {
      display: none;
    }
  }

  .mobile-menu-backdrop {
    display: block;
    position: fixed;
    inset: 56px 0 0;
    z-index: 1000;
    background: rgb(15 23 42 / 42%);
  }

  .mobile-menu-panel {
    display: flex;
    position: fixed;
    top: 56px;
    right: 0;
    bottom: 0;
    z-index: 1001;
    width: min(86vw, 340px);
    box-sizing: border-box;
    padding: 12px 18px max(24px, env(safe-area-inset-bottom));
    background: #fff;
    flex-direction: column;
    justify-content: space-between;
    overflow-y: auto;
    transform: translateX(100%);
    transition: transform 0.22s ease;
  }

  .mobile-menu-panel.visible {
    transform: translateX(0);
  }

  .mobile-menu-links button {
    display: flex;
    width: 100%;
    min-height: 48px;
    padding: 0 4px;
    border: 0;
    border-bottom: 1px solid #edf0f4;
    background: transparent;
    color: #30394a;
    font-size: 15px;
    align-items: center;
    justify-content: space-between;
  }

  .mobile-menu-links button.active {
    color: #0066ff;
    font-weight: 600;
  }

  .mobile-menu-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    padding-top: 24px;
  }

  .mobile-menu-actions a,
  .mobile-menu-actions button {
    display: inline-flex;
    min-height: 42px;
    padding: 0 12px;
    border-radius: 6px;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 14px;
    text-decoration: none;
  }

  .mobile-menu-actions a,
  .mobile-trial-button {
    grid-column: 1 / -1;
  }

  .mobile-menu-actions a {
    color: #0066ff;
    background: #edf5ff;
  }

  .mobile-trial-button {
    border: 1px solid #cbd5e1;
    color: #334155;
    background: #fff;
  }

  .mobile-login-button,
  .mobile-register-button {
    border: 0;
    color: #fff;
    background: #0066ff;
  }

  .mega-products-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .mega-solutions-layout {
    grid-template-columns: 1fr;
    min-height: 0;
  }

  .mega-menu-overlay.is-mega-wide {
    background: #fff;
  }

  .mega-menu-panel.is-mega-wide {
    padding: 16px;
  }

  .mega-solutions-sidebar {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 6px;
    padding: 12px;
    border-right: 0;
    border-radius: 8px;
  }

  .mega-solutions-tab {
    width: auto;
    padding: 8px 12px;
    font-size: 13px;
  }

  .mega-solutions-content {
    grid-template-columns: 1fr;
    padding: 16px 0 0;
    gap: 16px;
  }

  .mega-solutions-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .mega-featured-case {
    max-width: 360px;
  }
}

@media (max-width: 900px) {
  .navbar-actions .nav-service-phone span,
  .navbar-actions .btn-outline {
    display: none;
  }

  .navbar-actions .nav-service-phone {
    width: 36px;
    height: 36px;
    padding: 0;
    border-radius: 8px;
    justify-content: center;
  }
}

@media (max-width: 700px) {
  .brand-logo {
    width: 128px;
    height: 38px;
  }

  .navbar-actions {
    display: none;
  }
}
</style>
